import React from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { Form, Modal } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPaperclip } from '@fortawesome/pro-solid-svg-icons'

import Button from 'design-system/button'
import { ButtonSizes, ButtonVariant } from 'design-system/button/src/Button'
import { FileTemplate, TemplateProps } from './FileTemplate'

import styles from './UploadFile.module.scss'

interface UploadFileProps {
  title: React.ReactNode
  labels: {
    legend: React.ReactNode
    addFile: React.ReactNode
    close?: React.ReactNode
  }
  template?: TemplateProps
  help?: React.ReactNode
  show: boolean
  onClose?(): void
  onUpload?(file: File): void
  error?: React.ReactNode
}

const UploadFile: React.FC<UploadFileProps> = ({
  show,
  title,
  labels: { legend, addFile, close },
  template,
  help,
  onUpload,
  onClose,
  error,
}) => {
  const { t } = useTranslation()

  const handleClose = () => {
    onClose && onClose()
  }

  const handleFileUpload = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    if (!target.files) return
    const file = target.files.item(0)
    if (file) {
      onUpload?.(file)
    }
    target.value = ''
  }

  return (
    <>
      <Modal size="lg" show={show} onHide={handleClose} centered>
        <Modal.Header closeButton className="ml-3 mr-3 mt-2 pb-2">
          <Modal.Title>
            <p className={styles.headFont}>{title}</p>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="ml-3 mr-3">
          <p>{legend}</p>
          <Modal.Dialog
            size="lg"
            className="pt-1 mb-2"
            contentClassName={classNames(
              styles.modalContent,
              error && styles.error
            )}
          >
            <Form className={styles.inputDesign}>
              <Form.Group>
                <input
                  className={styles.inputFile}
                  id="customFile"
                  type="file"
                  onChange={handleFileUpload}
                />
                <Form.Label
                  htmlFor="customFile"
                  className={classNames(styles.labelFile)}
                  onDrop={(e) => {
                    e.preventDefault()
                    const file = e.dataTransfer.files.item(0)

                    if (file) onUpload?.(file)
                  }}
                  onDragOver={(e) => {
                    e.preventDefault()
                  }}
                >
                  <div className="h-100 d-flex align-items-center justify-content-center">
                    <FontAwesomeIcon icon={faPaperclip} className="mr-1" />
                    {addFile}
                  </div>
                </Form.Label>
              </Form.Group>
            </Form>
          </Modal.Dialog>
          {error && (
            // Animation for error message
            <p
              className={classNames(
                'lead small text-danger text-center',
                styles.fileError
              )}
            >
              {error}
            </p>
          )}
          {template && (
            <div className="d-flex justify-content-end">
              <FileTemplate {...template} />
            </div>
          )}
          {help}
        </Modal.Body>
        <Modal.Footer className="ml-3 mr-3 mt-2">
          <Button
            variant={ButtonVariant.Secondary}
            size={ButtonSizes.Small}
            onClick={handleClose}
          >
            {close || t('commons.buttons.close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default UploadFile
